import React from 'react';
import { Heart, Dumbbell, Scissors, Scale, Briefcase, GraduationCap, Wrench, Users, ArrowRight, CheckCircle, Target, Zap, TrendingUp, Shield } from 'lucide-react';

const NichesPage: React.FC = () => {
  const industries = [
    {
      icon: Heart,
      name: 'Healthcare & Medical',
      description: 'Patient management, medical records, appointment scheduling',
      features: [
        'Patient profiles with medical history',
        'Insurance tracking and billing',
        'Prescription management',
        'Medical forms and documentation',
        'HIPAA compliance features',
        'Lab results integration'
      ],
      examples: ['Medical clinics', 'Dental practices', 'Therapy centers', 'Veterinary clinics'],
      liveExample: {
        name: 'ClinicIT',
        url: 'https://clinicit.live',
        description: 'Live medical practice management platform serving real clinics with unified booking for online appointments and walk-in patients.'
      }
    },
    {
      icon: Dumbbell,
      name: 'Fitness & Wellness',
      description: 'Member management, class booking, trainer scheduling',
      features: [
        'Membership management and renewals',
        'Class scheduling and capacity limits',
        'Trainer assignments and schedules',
        'Progress tracking and goals',
        'Payment plans and billing',
        'Equipment booking and maintenance'
      ],
      examples: ['Gyms and fitness centers', 'Yoga studios', 'Personal training', 'Wellness centers']
    },
    {
      icon: Scissors,
      name: 'Beauty & Spa Services',
      description: 'Complete salon suite with service menus and stylist management',
      features: [
        'Service catalogs with pricing',
        'Stylist portfolios and specialties',
        'Before/after photo galleries',
        'Package deals and memberships',
        'Inventory management for products',
        'Client preference tracking'
      ],
      examples: ['Hair salons', 'Nail studios', 'Spas', 'Barbershops', 'Beauty clinics']
    },
    {
      icon: Scale,
      name: 'Legal & Professional Services',
      description: 'Client cases, consultation scheduling, document management',
      features: [
        'Case management and tracking',
        'Document templates and storage',
        'Billing hours and invoicing',
        'Court date scheduling',
        'Client communication logs',
        'Confidentiality and security'
      ],
      examples: ['Law firms', 'Accounting firms', 'Consulting practices', 'Real estate']
    },
    {
      icon: Briefcase,
      name: 'Consulting & Business Services',
      description: 'Project management, client meetings, proposal tracking',
      features: [
        'Project timeline management',
        'Proposal creation and tracking',
        'Meeting scheduling and notes',
        'Deliverable tracking',
        'Time tracking and billing',
        'Client portal for updates'
      ],
      examples: ['Business consultants', 'Marketing agencies', 'IT services', 'Financial advisors']
    },
    {
      icon: GraduationCap,
      name: 'Education & Training',
      description: 'Student management, class scheduling, progress tracking',
      features: [
        'Student enrollment and profiles',
        'Course scheduling and capacity',
        'Progress tracking and grades',
        'Assignment and homework management',
        'Parent/guardian communication',
        'Certification tracking'
      ],
      examples: ['Tutoring centers', 'Music schools', 'Language schools', 'Training institutes']
    },
    {
      icon: Wrench,
      name: 'Home Services',
      description: 'Job scheduling, customer management, technician dispatch',
      features: [
        'Service area mapping',
        'Technician scheduling and dispatch',
        'Equipment and inventory tracking',
        'Before/after job photos',
        'Estimate creation and approval',
        'Recurring service scheduling'
      ],
      examples: ['HVAC services', 'Plumbing', 'Electrical', 'Cleaning services', 'Landscaping']
    },
    {
      icon: Users,
      name: 'Custom Industries',
      description: 'Any business model - we adapt the platform to your needs',
      features: [
        'Custom workflow design',
        'Industry-specific terminology',
        'Specialized reporting needs',
        'Integration requirements',
        'Unique compliance needs',
        'Custom user roles'
      ],
      examples: ['Event planning', 'Photography', 'Food services', 'Pet services', 'And many more...']
    }
  ];

  const adaptationProcess = [
    {
      step: 1,
      title: 'Industry Analysis',
      description: 'We study your industry\'s specific needs and workflows'
    },
    {
      step: 2,
      title: 'Custom Configuration',
      description: 'Adapt the platform with industry-specific features and terminology'
    },
    {
      step: 3,
      title: 'Workflow Optimization',
      description: 'Optimize user flows for your business processes'
    },
    {
      step: 4,
      title: 'Testing & Refinement',
      description: 'Test all features to ensure perfect fit for your industry'
    }
  ];

  const benefits = [
    {
      title: 'Industry-Specific Language',
      description: 'All terminology adapted to your field - no generic business terms'
    },
    {
      title: 'Optimized Workflows',
      description: 'User flows designed around how your industry actually operates'
    },
    {
      title: 'Relevant Features Only',
      description: 'Hide irrelevant features, highlight what matters to your business'
    },
    {
      title: 'Professional Credibility',
      description: 'Clients see a platform built specifically for their industry'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-innovation text-white relative overflow-hidden">
      {/* Global Animated Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-transparent"></div>
      <div className="absolute top-20 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
      <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-purple-500/5 rounded-full blur-2xl animate-pulse" style={{animationDelay: '4s'}}></div>
      <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-blue-500/8 rounded-full blur-3xl animate-pulse" style={{animationDelay: '6s'}}></div>
      
      {/* Hero Section */}
      <section className="py-20 relative z-10 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white via-blue-100 to-cyan-300 bg-clip-text text-transparent">
            Your Industry,<br />Perfectly Optimized
          </h1>
          <p className="text-xl md:text-2xl text-slate-300 max-w-4xl mx-auto mb-8 leading-relaxed">
            Why settle for generic when you can have <span className="text-cyan-300 font-semibold">industry-specific perfection</span>? 
            Our platform adapts its features, terminology, and workflows to <span className="text-yellow-300 font-semibold">speak your industry's language</span> 
            and serve your unique needs.
          </p>
          <div className="mb-8 flex flex-col sm:flex-row gap-4 justify-center">
            <div className="bg-gradient-to-r from-emerald-500/20 to-green-400/20 backdrop-blur-xl border border-emerald-300/30 text-white px-8 py-4 rounded-2xl text-lg font-bold shadow-2xl hover:shadow-emerald-500/25 transition-all duration-300 hover:scale-105">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                <Target className="h-5 w-5 text-emerald-400" />
                500+ Industries Served
              </div>
            </div>
            <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-xl border border-blue-300/30 text-white px-8 py-4 rounded-2xl text-lg font-bold shadow-2xl hover:shadow-blue-500/25 transition-all duration-300 hover:scale-105">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                <Zap className="h-5 w-5 text-blue-400" />
                Custom Workflows
              </div>
            </div>
            <div className="bg-gradient-to-r from-orange-500/20 to-yellow-500/20 backdrop-blur-xl border border-orange-300/30 text-white px-8 py-4 rounded-2xl text-lg font-bold shadow-2xl hover:shadow-orange-500/25 transition-all duration-300 hover:scale-105">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
                <TrendingUp className="h-5 w-5 text-orange-400" />
                Industry Expertise
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Popular Industry Adaptations
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              See how we customize the platform for different industries with specific features and workflows
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {industries.map((industry, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10/5 backdrop-blur-sm border border-white/10 rounded-2xl shadow-xl p-8 hover:shadow-xl transition-shadow duration-200">
                <div className="flex items-center mb-6">
                  <div className="bg-cyan-100 p-3 rounded-lg mr-4">
                    <industry.icon className="h-8 w-8 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{industry.name}</h3>
                    <p className="text-slate-300">{industry.description}</p>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-semibold text-white mb-3">Custom Features:</h4>
                  <ul className="space-y-2">
                    {industry.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start space-x-3">
                        <CheckCircle className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-200 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-4 rounded-lg">
                  <h4 className="font-semibold text-white mb-2">Perfect for:</h4>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {industry.examples.map((example, exampleIndex) => (
                      <span key={exampleIndex} className="bg-cyan-100 text-cyan-900 px-3 py-1 rounded-full text-xs font-medium">
                        {example}
                      </span>
                    ))}
                  </div>
                  {industry.liveExample && (
                    <div className="border-t border-white/10 pt-3 mt-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                            <span className="text-green-400 font-semibold text-sm">LIVE EXAMPLE</span>
                          </div>
                          <p className="text-slate-300 text-sm">{industry.liveExample.description}</p>
                        </div>
                        <a 
                          href={industry.liveExample.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-200 flex items-center gap-2 ml-4 flex-shrink-0"
                        >
                          Visit {industry.liveExample.name}
                          <ArrowRight className="h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Live ClinicIT Demo */}
      <section className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-green-400 font-bold text-lg">LIVE HEALTHCARE EXAMPLE</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              ClinicIT - Medical Practice Platform
            </h2>
            <p className="text-xl text-slate-300 max-w-4xl mx-auto mb-8">
              See how our platform was customized for the healthcare industry. This is a real, live system 
              <span className="text-cyan-300 font-semibold"> serving actual medical clinics</span> with HIPAA compliance and specialized features.
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-cyan-400/30 transition-all duration-300">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-3 rounded-xl">
                  <Heart className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">Healthcare Industry Customization</h3>
                  <p className="text-slate-300">HIPAA-compliant unified booking and patient management</p>
                </div>
              </div>
              <a 
                href="https://clinicit.live" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center gap-2"
              >
                Open Full Site
                <ArrowRight className="h-5 w-5" />
              </a>
            </div>

            <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="flex gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
                <div className="bg-slate-800 rounded px-3 py-1 text-slate-300 text-sm font-mono">
                  https://clinicit.live
                </div>
              </div>
              
              <div className="relative">
                <iframe
                  src="https://clinicit.live"
                  className="w-full h-[600px] rounded-lg border border-slate-600/50"
                  title="ClinicIT Live Healthcare Demo"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-transparent via-transparent to-slate-900/10 pointer-events-none rounded-lg"></div>
              </div>
            </div>

            <div className="grid md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center">
                <div className="bg-blue-500/20 p-2 rounded-lg w-fit mx-auto mb-3">
                  <Users className="h-6 w-6 text-blue-400" />
                </div>
                <h4 className="text-sm font-bold text-white mb-1">Patient Management</h4>
                <p className="text-slate-300 text-xs">Complete medical records</p>
              </div>
              
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center">
                <div className="bg-green-500/20 p-2 rounded-lg w-fit mx-auto mb-3">
                  <Shield className="h-6 w-6 text-green-400" />
                </div>
                <h4 className="text-sm font-bold text-white mb-1">HIPAA Compliant</h4>
                <p className="text-slate-300 text-xs">Medical-grade security</p>
              </div>
              
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center">
                <div className="bg-purple-500/20 p-2 rounded-lg w-fit mx-auto mb-3">
                  <Target className="h-6 w-6 text-purple-400" />
                </div>
                <h4 className="text-sm font-bold text-white mb-1">Unified Booking</h4>
                <p className="text-slate-300 text-xs">Online + Walk-in patients</p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center">
                <div className="bg-cyan-500/20 p-2 rounded-lg w-fit mx-auto mb-3">
                  <Zap className="h-6 w-6 text-cyan-400" />
                </div>
                <h4 className="text-sm font-bold text-white mb-1">Live Production</h4>
                <p className="text-slate-300 text-xs">Serving real clinics</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-400/20 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <TrendingUp className="h-6 w-6 text-cyan-400" />
                <h4 className="text-xl font-bold text-white">Your Industry Could Be Next</h4>
              </div>
              <p className="text-slate-300 mb-4">
                ClinicIT isn't reselling our platform - they created their own branded medical SaaS by customizing it. 
                You can do the same: transform it into <strong className="text-yellow-300">YOUR niche-specific solution</strong>.
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="bg-cyan-400/20 text-cyan-300 px-3 py-1 rounded-full text-sm font-medium">Industry Terminology</span>
                <span className="bg-cyan-400/20 text-cyan-300 px-3 py-1 rounded-full text-sm font-medium">Specialized Workflows</span>
                <span className="bg-cyan-400/20 text-cyan-300 px-3 py-1 rounded-full text-sm font-medium">Compliance Features</span>
                <span className="bg-cyan-400/20 text-cyan-300 px-3 py-1 rounded-full text-sm font-medium">Custom Branding</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Adaptation Process */}
      <section className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              How We Adapt the Platform
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Our systematic process ensures your platform feels like it was built exclusively for your industry
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {adaptationProcess.map((step, index) => (
              <div key={index} className="text-center">
                <div className="bg-primary-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-bold">
                  {step.step}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                <p className="text-slate-300">{step.description}</p>
                {index < adaptationProcess.length - 1 && (
                  <ArrowRight className="hidden md:block h-6 w-6 text-slate-400 mx-auto mt-4" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Benefits */}
      <section className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Why Industry Customization Matters
            </h2>
            <p className="text-xl text-slate-300">
              Generic software feels generic. Industry-specific platforms build trust and credibility
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
                <p className="text-slate-300">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Real Examples Section */}
      <section className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Real Customization Examples
            </h2>
            <p className="text-xl text-slate-300">
              See the difference industry-specific customization makes
            </p>
          </div>

          <div className="space-y-12">
            {/* Healthcare Example */}
            <div className="bg-blue-50 rounded-2xl p-8">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="flex items-center mb-4">
                    <Heart className="h-8 w-8 text-blue-600 mr-3" />
                    <h3 className="text-2xl font-bold text-slate-900">Healthcare Clinic</h3>
                  </div>
                  <h4 className="text-lg font-semibold text-slate-800 mb-2">Generic vs. Customized:</h4>
                  <div className="space-y-2 text-slate-700">
                    <p><span className="line-through text-red-500">Customers</span> → <span className="text-green-700 font-medium">Patients</span></p>
                    <p><span className="line-through text-red-500">Appointments</span> → <span className="text-green-700 font-medium">Medical Appointments</span></p>
                    <p><span className="line-through text-red-500">Services</span> → <span className="text-green-700 font-medium">Medical Procedures</span></p>
                    <p><span className="line-through text-red-500">Notes</span> → <span className="text-green-700 font-medium">Medical Records</span></p>
                  </div>
                </div>
                <div className="bg-white/80 backdrop-blur-sm border border-slate-200 p-6 rounded-xl shadow-sm">
                  <h4 className="font-semibold text-slate-900 mb-3">Added Features:</h4>
                  <ul className="space-y-2">
                    <li className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span className="text-sm text-slate-700">Insurance verification system</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span className="text-sm text-slate-700">Prescription management</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span className="text-sm text-slate-700">Medical history tracking</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span className="text-sm text-slate-700">HIPAA compliance tools</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Fitness Example */}
            <div className="bg-green-50 rounded-2xl p-8">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="flex items-center mb-4">
                    <Dumbbell className="h-8 w-8 text-green-600 mr-3" />
                    <h3 className="text-2xl font-bold text-slate-900">Fitness Studio</h3>
                  </div>
                  <h4 className="text-lg font-semibold text-slate-800 mb-2">Generic vs. Customized:</h4>
                  <div className="space-y-2 text-slate-700">
                    <p><span className="line-through text-red-500">Clients</span> → <span className="text-green-700 font-medium">Members</span></p>
                    <p><span className="line-through text-red-500">Services</span> → <span className="text-green-700 font-medium">Classes & Training</span></p>
                    <p><span className="line-through text-red-500">Staff</span> → <span className="text-green-700 font-medium">Trainers & Instructors</span></p>
                    <p><span className="line-through text-red-500">Bookings</span> → <span className="text-green-700 font-medium">Class Reservations</span></p>
                  </div>
                </div>
                <div className="bg-white/80 backdrop-blur-sm border border-slate-200 p-6 rounded-xl shadow-sm">
                  <h4 className="font-semibold text-slate-900 mb-3">Added Features:</h4>
                  <ul className="space-y-2">
                    <li className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-slate-700">Membership management</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-slate-700">Class capacity limits</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-slate-700">Progress tracking</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-slate-700">Equipment booking</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Custom Industry CTA */}
      <section className="py-20 relative z-10">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
            Don't See Your Industry?
          </h2>
          <p className="text-xl mb-8 text-slate-300">
            We can customize the platform for any business model or industry. 
            Just tell us your requirements and we'll make it perfect for your niche.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/pricing"
              className="bg-yellow-400 text-slate-900 px-8 py-4 rounded-lg text-lg font-bold hover:bg-yellow-300 transition-colors duration-200"
            >
              Get Custom Platform
            </a>
            <a
              href="/demo"
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-white/5 backdrop-blur-sm border border-white/10 hover:text-cyan-400 transition-colors duration-200"
            >
              Schedule Consultation
            </a>
          </div>
        </div>
      </section>

      {/* Success Stories Teaser */}
      <section className="py-16 relative z-10">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-4">
            See How Others Succeeded
          </h2>
          <p className="text-slate-300 mb-6">
            Read success stories from partners who chose industry-specific customization
          </p>
          <a
            href="/success-stories"
            className="text-cyan-400 hover:text-cyan-400 font-semibold inline-flex items-center"
          >
            View Success Stories
            <ArrowRight className="ml-2 h-4 w-4" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default NichesPage;

